import React, { useMemo } from 'react';
import {
  ArrowRightOutlined,
  BankOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DesktopOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  LinkOutlined,
  LockOutlined,
  MessageOutlined,
  ProfileOutlined,
  RiseOutlined,
  RobotOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  StarOutlined,
  SyncOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  WarningOutlined
} from '@ant-design/icons';
import styled from 'styled-components';

import usePerx3Redesign from 'hooks/usePerx3Redesign';

/**
 * The monochrome stand-in for every coloured glyph Orbit prints. Most of them are baked into the
 * translated strings and into the filter signatures the Intelligence screens publish, neither of
 * which may change: flag OFF has to keep printing the glyph verbatim, so the swap happens at render
 * time and this map is the only place that knows which icon each glyph becomes.
 */
const GLYPH_ICONS: Record<string, React.ComponentType> = {
  '🖥': DesktopOutlined,
  '📅': CalendarOutlined,
  '📊': BarChartOutlined,
  '📈': RiseOutlined,
  '🤖': RobotOutlined,
  '⚠': WarningOutlined,
  '❗': ExclamationCircleOutlined,
  '✦': StarOutlined,
  '↗': ExportOutlined,
  '🔒': LockOutlined,
  '🔄': SyncOutlined,
  '🔍': SearchOutlined,
  '➡': ArrowRightOutlined,
  '💬': MessageOutlined,
  '🛒': ShoppingCartOutlined,
  '💎': StarOutlined,
  '⚡': ThunderboltOutlined,
  '🔗': LinkOutlined,
  '👥': TeamOutlined,
  '✅': CheckCircleOutlined,
  '📋': ProfileOutlined,
  '🏦': BankOutlined
};

const GLYPHS = Object.keys(GLYPH_ICONS);

export interface OrbitGlyphSplit {
  /** The icon the leading glyph maps to, absent when the string opens on anything else. */
  Icon?: React.ComponentType;
  /** The string without its leading glyph, or the string untouched when there was none. */
  rest: string;
}

/**
 * Splits a leading glyph off a string. A variation selector may follow the glyph (`⚠️` is `⚠` plus
 * U+FE0F) and is dropped with it, along with the space the copy puts between glyph and words.
 */
export const splitOrbitGlyph = (text: string): OrbitGlyphSplit => {
  const glyph = GLYPHS.find(candidate => text.startsWith(candidate));

  if (glyph === undefined) {
    return { rest: text };
  }

  return {
    Icon: GLYPH_ICONS[glyph],
    rest: text.slice(glyph.length).replace(/^️?\s*/, '')
  };
};

const GlyphIcon = styled.span`
  margin-right: 6px;
  color: currentColor;
  font-size: 1em;
`;

export interface IOrbitGlyphIconProps {
  Icon: React.ComponentType;
}

export const OrbitGlyphIcon: React.FC<IOrbitGlyphIconProps> = ({ Icon }) => (
  <GlyphIcon aria-hidden="true">
    <Icon />
  </GlyphIcon>
);

export interface IOrbitGlyphTextProps {
  text: string;
}

const OrbitGlyphText: React.FC<IOrbitGlyphTextProps> = ({ text }) => {
  const perx3 = usePerx3Redesign();
  const split = useMemo(() => (perx3 ? splitOrbitGlyph(text) : undefined), [perx3, text]);

  if (split?.Icon === undefined) {
    return <>{text}</>;
  }

  return (
    <>
      <OrbitGlyphIcon Icon={split.Icon} />
      {split.rest}
    </>
  );
};

export default OrbitGlyphText;
